import type { ComponentType, ReactNode } from 'react';
import { Box, Typography } from '@mui/material';
import TimelineRecordFormItem from './TimelineRecordFormItem';
import TimelineRecordItem from './TimelineRecordItem';
import type { TimelineEntry } from '../internalHooks/useTimelineEntries';
import type { TimelineRecordAuthorProps } from '../types/configTypes';
import type { TimelineItem } from '../types/types';

export interface TimelineFormItem {
  item: TimelineItem;
  content: ReactNode;
  tag: ReactNode;
  tagColor?: TimelineItem['tagColor'];
}

interface TimelineItemsProps {
  instanceId: string;
  entries: TimelineEntry[];
  RecordAuthor: ComponentType<TimelineRecordAuthorProps>;
  emptyStateText: string;
  addForm?: TimelineFormItem;
  editForm?: TimelineFormItem;
  highlightedEntryId?: TimelineItem['id'];
  onPinned: (item: TimelineItem, isPinned: boolean) => void;
}

const TimelineItems = ({
  instanceId,
  entries,
  RecordAuthor,
  emptyStateText,
  addForm,
  editForm,
  highlightedEntryId,
  onPinned,
}: TimelineItemsProps) => (
  <>
    <Box role="list" aria-label="Timeline">
      {addForm && (
        <Box role="listitem">
          <TimelineRecordFormItem
            item={addForm.item}
            RecordAuthor={RecordAuthor}
            tag={addForm.tag}
            tagColor={addForm.tagColor}
            isAdd
          >
            {addForm.content}
          </TimelineRecordFormItem>
        </Box>
      )}
      {entries.map(({ item, isPinned }) => {
        const isHighlighted = highlightedEntryId !== undefined && String(item.id) === String(highlightedEntryId);

        return (
          <Box
            id={`timeline-${instanceId}-entry-${item.id}`}
            data-timeline-entry-id={String(item.id)}
            role="listitem"
            key={item.id}
          >
            {editForm?.item.id === item.id ? (
              <TimelineRecordFormItem
                item={item}
                RecordAuthor={RecordAuthor}
                isHighlighted={isHighlighted}
                tag={editForm.tag}
                tagColor={editForm.tagColor}
              >
                {editForm.content}
              </TimelineRecordFormItem>
            ) : (
              <TimelineRecordItem
                item={item}
                RecordAuthor={RecordAuthor}
                isPinned={isPinned}
                isHighlighted={isHighlighted}
                onPinned={nextPinned => onPinned(item, nextPinned)}
              />
            )}
          </Box>
        );
      })}
    </Box>
    {!entries.length && !addForm && <Typography color="text.secondary">{emptyStateText}</Typography>}
  </>
);

export default TimelineItems;
